import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../mendepartmentdetails/Sidebar';
import SubDeptImage from '../../../assets/image/department/employees-giving-hands-helping-colleagues-walk-upstairs_74855-5236.jpg';

function YouthDevelopmentMentorship() {
  const [Faq, setFaq] = useState(0); // first dropdown open by default
  const titleRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');

  useEffect(() => {
    if (titleRef.current) {
      const c = window.getComputedStyle(titleRef.current).color;
      setTitleColor(c);
    }
  }, []);

  // shared inline styles (consistent with other extended pages)
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.35, color: '#0f172a' };
  const valText = { fontSize: '1rem', lineHeight: 1.6, color: '#334155' };
  const overviewText = { color: titleColor || '#0f172a', fontSize: '1.05rem', lineHeight: 1.6, margin: '8px 0 16px' };

  const sections = [
    {
      Que: 'Program Models',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['1:1 mentoring:', 'Matched youth–mentor pairs with clear goals, regular check-ins, and parent/guardian alignment.'],
            ['Group mentoring:', 'Small groups led by trained mentors; peer support, teamwork, and leadership roles.'],
            ['Peer & near-peer mentoring:', 'Older students or recent grads coach younger youth with staff oversight.'],
          ].map(([k, v], i) => (
            <li key={`models-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Enrollment & Eligibility',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Ages served:', 'Typically middle and high school (11–18); some sites include grades 4–5 and first-year college.'],
            ['Parent/guardian consent:', 'Required for minors; include emergency contacts and permissions for trips/virtual sessions.'],
            ['Fit & goals:', 'Simple interest survey to align youth interests with mentor strengths.'],
          ].map(([k, v], i) => (
            <li key={`enroll-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Leadership & Life Skills',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Communication & confidence:', 'Public speaking, networking basics, and constructive feedback.'],
            ['Time & project management:', 'Goal setting, weekly planning, and accountability check-ins.'],
            ['Civic engagement:', 'Service projects, youth councils, and community presentations.'],
          ].map(([k, v], i) => (
            <li key={`life-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Academic & College/Career Readiness',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Tutoring & study labs:', 'Homework support, test prep, and executive-function coaching.'],
            ['Postsecondary planning:', 'Campus visits, dual-enrollment awareness, FAFSA/scholarship guidance.'],
            ['Career exploration:', 'Guest speakers, career inventories, and skill-to-occupation mapping.'],
          ].map(([k, v], i) => (
            <li key={`academic-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Work-Based Learning & First Jobs',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Job shadowing & site tours:', 'Short exposures to workplaces with reflective assignments.'],
            ['Internships & service learning:', 'Project-based roles with supervisor mentorship and evaluations.'],
            ['Career tools:', 'Resumes, applications, mock interviews, and LinkedIn basics for teens.'],
          ].map(([k, v], i) => (
            <li key={`wbl-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Safety, Screening & Safeguarding',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Mentor screening:', 'References, background checks, training, and code-of-conduct acknowledgment.'],
            ['Two-adult rule & boundaries:', 'Approved meeting locations, chaperone ratios, and documented communication.'],
            ['Reporting & supports:', 'Clear incident reporting, mandated-reporter guidance, and escalation paths.'],
          ].map(([k, v], i) => (
            <li key={`safety-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Inclusion & Accessibility',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Language access:', 'Interpreter support and translated materials upon request.'],
            ['Disability accommodations:', 'Accessible spaces, visual schedules, and sensory-friendly options.'],
            ['Cultural responsiveness:', 'Mentor training on identity, bias awareness, and inclusive practices.'],
          ].map(([k, v], i) => (
            <li key={`inclusion-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Family Engagement',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Orientation & updates:', 'Program overview, schedules, and consistent communication with families.'],
            ['Workshops:', 'Financial literacy, digital safety, and postsecondary planning sessions.'],
            ['Celebrations:', 'Showcase nights recognizing youth milestones and mentor contributions.'],
          ].map(([k, v], i) => (
            <li key={`family-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Measurement & Outcomes',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Attendance & engagement:', 'Track participation, session quality, and goal progress.'],
            ['Academic & career indicators:', 'Grades, credits, certifications, internships, and job placements.'],
            ['Feedback loops:', 'Youth/mentor surveys and continuous improvement cycles.'],
          ].map(([k, v], i) => (
            <li key={`kpi-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Aftercare & Alumni',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Transition support:', 'Check-ins after graduation for college/work onboarding and referrals.'],
            ['Alumni network:', 'Near-peer mentors, panels, and networking events.'],
            ['Micro-credentials:', 'Short courses to upskill and document competencies.'],
          ].map(([k, v], i) => (
            <li key={`alumni-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <section className="department-details-section">
      <div className="container">
        <div className="row">

          {/* Sidebar */}
          <div className="col-12 col-lg-4 col-xl-4">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="col-lg-8">
            <div className="department-details-imgbox">
              <img src={SubDeptImage} className="img-fluid" alt="Youth Development and Mentorship" />
            </div>

            <div className="department-details-content-box">
              <h4 ref={titleRef} className="department-details-title">Youth Development and Mentorship</h4>

              {/* Overview (always visible, same color as title but smaller) */}
              <p style={overviewText}>
                Structured mentoring, leadership practice, and career exploration—so youth build confidence, expand their
                networks, and take concrete next steps toward college, training, or first jobs.
              </p>
            </div>

            {/* Accordion (expanded information) */}
            <div className="accordian-box">
              {sections.map((item, index) => (
                <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                  <div className="accordian-title">
                    <h5>{item.Que}</h5>
                    <i
                      className="fa-solid fa-angle-right"
                      onClick={() => setFaq(index)}
                      role="button"
                      aria-label={`Toggle ${item.Que}`}
                    />
                  </div>

                  {Faq === index && (
                    <div className="accordian-content" style={{ display: 'block' }}>
                      {item.Answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="department-details-policy-box">
              <h4>Our Commitment:</h4>
              <p>
                We pair young people with trained mentors, deliver inclusive programming, and measure outcomes—so every
                participant gains skills, support, and momentum that lasts.
              </p>
            </div>

            <div>
              <Link to="/department/education">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }} />
                Go Back
              </Link>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

export default YouthDevelopmentMentorship;
